import React, { useState } from 'react';
import { View, Text, Image, TouchableOpacity, StyleSheet } from 'react-native';
import { CarouselDots } from '@/components/residents/marketplace/CarouselDots';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';

interface MaintenanceImageCarouselProps {
  images: string[];
  variant?: 'mobile' | 'desktop';
}

// Chevron Right Icon for Mobile
const ChevronRightIcon: React.FC = () => (
  <View style={styles.chevronIcon}>
    <View style={styles.chevronArrow} />
  </View>
);

/**
 * MaintenanceImageCarousel - Image carousel for maintenance ticket attachments
 * Used in both mobile and desktop detail views
 */
export const MaintenanceImageCarousel: React.FC<MaintenanceImageCarouselProps> = ({
  images,
  variant = 'mobile'
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Desktop shows a placeholder when no images
  if (images.length === 0 && variant === 'desktop') {
    return (
      <View style={styles.desktopNoImagePlaceholder}>
        <RESIDENTS_SVG_COMPONENTS.MAINTENANCE width={48} height={48} color={PRIMITIVE_COLORS.gray500} />
        <Text style={styles.desktopNoImageText}>No images attached</Text>
      </View>
    );
  }

  // Mobile shows nothing when no images
  if (images.length === 0) {
    return null;
  }

  return (
    <View style={styles.imageContainer}>
      <View style={variant === 'mobile' ? styles.mobileImagePreview : styles.desktopImagePreview}>
        <Image
          source={{ uri: images[currentImageIndex] }}
          style={styles.image}
          resizeMode="cover"
        />
      </View>
      {images.length > 1 && (
        <View style={variant === 'mobile' ? styles.mobileDotsBar : styles.desktopDotsBar}>
          <CarouselDots
            count={images.length}
            activeIndex={currentImageIndex}
            onDotPress={setCurrentImageIndex}
            showBackground={false}
          />
          <TouchableOpacity 
            style={styles.imageNav}
            onPress={() => setCurrentImageIndex((prev) => (prev + 1) % images.length)}
            activeOpacity={0.7}
          >
            <Text style={variant === 'mobile' ? styles.mobileImageNavText : styles.desktopImageNavText}>
              NEXT
            </Text>
            {variant === 'desktop' ? (
              <RESIDENTS_SVG_COMPONENTS.CHEVRON_RIGHT width={20} height={20} color={PRIMITIVE_COLORS.gray300} />
            ) : (
              <ChevronRightIcon />
            )}
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  // Shared styles
  imageContainer: {
    borderRadius: 8,
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  imageNav: {
    position: 'absolute',
    right: 16,
    flexDirection: 'row',
    alignItems: 'center',
  },
  chevronIcon: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chevronArrow: {
    width: 0,
    height: 0,
    borderTopWidth: 5,
    borderBottomWidth: 5,
    borderLeftWidth: 6,
    borderStyle: 'solid',
    borderTopColor: 'transparent',
    borderBottomColor: 'transparent',
    borderLeftColor: MARKETPLACE_COLORS.textSecondary,
  },

  // Mobile styles
  mobileImagePreview: {
    height: 281,
    backgroundColor: MARKETPLACE_COLORS.uploadAreaBackground,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  mobileDotsBar: {
    backgroundColor: MARKETPLACE_COLORS.uploadAreaBackground,
    borderTopWidth: 1,
    borderTopColor: MARKETPLACE_COLORS.uploadBorderWhite,
    height: 48,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },
  mobileImageNavText: {
    fontSize: 14,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.textSecondary,
  },

  // Desktop styles
  desktopImagePreview: {
    height: 302,
    backgroundColor: PRIMITIVE_COLORS.gray850,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  desktopDotsBar: {
    backgroundColor: PRIMITIVE_COLORS.gray850,
    borderTopWidth: 1,
    borderTopColor: PRIMITIVE_COLORS.white25,
    height: 48,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },
  desktopImageNavText: {
    fontSize: 14,
    fontWeight: '500',
    color: PRIMITIVE_COLORS.gray300,
  },
  desktopNoImagePlaceholder: {
    height: 302,
    backgroundColor: PRIMITIVE_COLORS.gray850,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
  },
  desktopNoImageText: {
    fontSize: 14,
    color: PRIMITIVE_COLORS.gray500,
  },
});
